import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { Autocomplete, Box, Chip, FilterOptionsState, TextField, Typography } from '@mui/material';

import { UserResult } from 'stores/UserStoreAdapter';

// --- 共通Props ---

type UserSelectBaseProps = {
  /** 全ユーザーリスト */
  users: UserResult[];
  /** ラベル */
  label?: string;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
};

// --- 複数選択Props ---

type UserSelectMultiProps = UserSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中のユーザーIDリスト */
  selectedUserIds: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (userIds: string[]) => void;
};

// --- 単一選択Props ---

type UserSelectSingleProps = UserSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中のユーザーID（null=未選択） */
  selectedUserId: string | null;
  /** 選択変更ハンドラ（単一） */
  onChange: (userId: string | null) => void;
};

export type UserSelectProps = UserSelectMultiProps | UserSelectSingleProps;

/**
 * ユーザー選択コンポーネント
 *
 * Autocomplete ベースでユーザーを検索・選択可能。
 * mode="multi" で複数選択、mode="single" で単一選択。
 * ユーザー名・ID・メールアドレスで絞り込みができる。
 */
const UserSelect: React.FC<UserSelectProps> = (props) => {
  const {
    users,
    label = '対象ユーザー',
    error = false,
    helperText,
    disabled = false,
    required = false,
  } = props;

  const filterOptions = (options: UserResult[], state: FilterOptionsState<UserResult>) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (u) =>
        u.userName.toLowerCase().includes(input) ||
        u.userId.toLowerCase().includes(input) ||
        u.mailAddress.toLowerCase().includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: UserResult,
  ) => {
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.userName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {option.mailAddress}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (props.mode === 'single') {
    const selectedUser = users.find((u) => u.userId === props.selectedUserId) || null;

    return (
      <Autocomplete
        options={users}
        value={selectedUser}
        onChange={(_, newValue) => props.onChange(newValue?.userId || null)}
        getOptionLabel={(option) => option.userName}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.userId === value.userId}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="ユーザーを検索..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        renderOption={renderOption}
        noOptionsText="該当するユーザーがありません"
        sx={{ width: '100%' }}
      />
    );
  }

  // mode === 'multi'
  const selectedUsers = users.filter((u) => props.selectedUserIds.includes(u.userId));

  return (
    <Autocomplete
      multiple
      options={users}
      value={selectedUsers}
      onChange={(_, newValue) => props.onChange(newValue.map((u) => u.userId))}
      getOptionLabel={(option) => option.userName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.userId === value.userId}
      disabled={disabled}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedUsers.length === 0 ? 'ユーザーを検索...' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      renderTags={(value, getTagProps) =>
        value.map((user, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={user.userName}
              size="small"
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      noOptionsText="該当するユーザーがありません"
      sx={{ width: '100%' }}
    />
  );
};

export default UserSelect;
